import { FC, useCallback } from 'react';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import ServiceTypeFilter from '@rover/components/src/formFields/ServiceTypeFilter';
import { LOCATION_DEFAULT_TITLE_2 } from '@rover/constants/src/location/location.constants';
import { DROP_OFF_PICK_UP_SUPPORTED_SERVICES } from '@rover/constants/src/search/searchPage.constants';
import CarouselRightSmall from '@rover/icons/svg/carousel-right-small.svg';
import { Button, Flex } from '@rover/kibble/core';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import {
  DayTimeAvailability,
  DaytimeServiceScheduler,
  FilterWrapper,
  Location,
  OvernightDateRange,
} from '@rover/react-lib/src/pages/search/SearchPage/components/Filters';
import { useSearchWizard } from '@rover/react-lib/src/pages/search/SearchPage/components/Modals/SearchWizardModal/context';
import getMaxRequestDate from '@rover/react-lib/src/utils/getMaxRequestDate';
import type { DateRange } from '@rover/types/src/datetime/DateRange';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

export const ServiceDetails: FC = () => {
  const { i18n } = useLingui();
  const {
    searchFilters,
    serviceTypeFilterOptions,
    serviceTypeOptions,
    showControls: { showDateRange, showDaytimeServiceScheduler, showDaytimeAvailability },
    serviceType,
    isLastStep,
    updateFilters,
    handleSubmit,
  } = useSearchWizard();
  const { locale } = useI18nContext();
  const maxDate = getMaxRequestDate();
  const { morningAvailability, middayAvailability, eveningAvailability } = serviceTypeFilterOptions;

  const startPlaceholder = DROP_OFF_PICK_UP_SUPPORTED_SERVICES.has(searchFilters.serviceType)
    ? t`Drop off`
    : t`Start`;
  const endPlaceholder = DROP_OFF_PICK_UP_SUPPORTED_SERVICES.has(searchFilters.serviceType)
    ? t`Pick up`
    : t`End`;

  const handleChangeDateRange = useCallback(
    (dateRange: Partial<DateRange>): void => {
      if (dateRange) {
        updateFilters({
          startDate: dateRange.startDate || undefined,
          endDate: dateRange.endDate || undefined,
        });
      }
    },
    [updateFilters]
  );
  const handlePropChange = useCallback(
    (key: keyof SearchFilters) => (value: SearchFilters[keyof SearchFilters]) => {
      updateFilters({ [key]: value });
    },
    [updateFilters]
  );

  return (
    <>
      <FilterWrapper>
        <ServiceTypeFilter
          displayMode="horizontal"
          serviceTypeOptions={serviceTypeOptions}
          label={t`Which service do you need?`}
          searchFilters={searchFilters}
          onChange={updateFilters}
        />
      </FilterWrapper>
      <FilterWrapper>
        <Location
          locationInputId="location-input-wizard"
          label={i18n._(LOCATION_DEFAULT_TITLE_2)}
          location={searchFilters.location}
          onChange={updateFilters}
        />
      </FilterWrapper>
      {showDateRange && (
        <FilterWrapper>
          <OvernightDateRange
            endPlaceholder={endPlaceholder}
            maxDate={maxDate}
            label={t`Which dates do you need?`}
            locale={locale}
            onChange={handleChangeDateRange}
            searchFilters={searchFilters}
            startPlaceholder={startPlaceholder}
          />
        </FilterWrapper>
      )}
      {showDaytimeServiceScheduler && (
        <FilterWrapper>
          <DaytimeServiceScheduler
            displayAs="compact"
            endPlaceholder={endPlaceholder}
            locale={locale}
            maxDate={maxDate}
            onChange={handlePropChange('frequency')}
            onDateRangeChange={handleChangeDateRange}
            oneTimeLabel={t`Which dates do you need?`}
            onSelectedDaysChange={handlePropChange('selectedDays')}
            onStartDateChange={handleChangeDateRange}
            repeatStartDateLabel={startPlaceholder}
            searchFilters={searchFilters}
            serviceType={serviceType}
            showArrow={false}
            showLabel
            startPlaceholder={startPlaceholder}
          />
        </FilterWrapper>
      )}
      {showDaytimeAvailability && (
        <FilterWrapper>
          <DayTimeAvailability
            availabilityBuckets={{
              morning: morningAvailability?.label || null,
              midday: middayAvailability?.label || null,
              evening: eveningAvailability?.label || null,
            }}
            label={t`Which time(s) do you need?`}
            labelIsVisible
            onChange={updateFilters}
            searchFilters={searchFilters}
          />
        </FilterWrapper>
      )}
      <Flex justifyContent="flex-end">
        <Button
          variant="primary"
          onClick={handleSubmit}
          icon={CarouselRightSmall}
          iconAlignment="right"
        >
          {isLastStep ? t`Search Now` : t`Next`}
        </Button>
      </Flex>
    </>
  );
};
